import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Send, CheckCircle, ArrowLeft, Upload, Shield, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';

const IncidentDeclarationPage = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    category: '',
    severity: '',
    patient_initials: '',
    date_incident: '',
    lens_type: '',
    lens_brand: '',
    description: '',
    consequences: '',
    actions_taken: '',
    reporter_name: user ? `${user.first_name} ${user.last_name}` : '',
    reporter_email: user?.email || '',
    anonymous: false,
  });

  const categories = [
    'Complication infectieuse',
    'Complication mecanique',
    'Reaction allergique',
    'Defaut de produit',
    'Erreur de prescription',
    'Probleme d\'adaptation',
    'Autre',
  ];

  const severities = [
    { value: 'low', label: 'Mineure', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    { value: 'medium', label: 'Moderee', color: 'bg-orange-50 text-orange-700 border-orange-200' },
    { value: 'high', label: 'Grave', color: 'bg-red-50 text-red-700 border-red-200' },
  ];

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.severity || !form.description) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/incidents`, form, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSubmitted(true);
      toast.success('Declaration envoyée avec succes');
    } catch {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', 'Roboto', sans-serif" }} data-testid="incident-page">
      <SiteHeader />

      {/* Hero */}
      <div className="relative overflow-hidden bg-[#1e3a5f]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-80 h-80 rounded-full bg-red-400/30 blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 py-14 sm:py-18 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-xs font-semibold tracking-wider uppercase mb-5">
            <Shield size={14} />
            Pharmacovigilance
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 tracking-tight">
            Declaration d'incident
          </h1>
          <p className="text-sm sm:text-base text-white/60 max-w-lg mx-auto">
            Signalez un incident lié aux lentilles de contact pour contribuer à la sécurité des patients
          </p>
        </div>
        <svg viewBox="0 0 1440 50" preserveAspectRatio="none" className="block w-full h-[35px] relative z-10">
          <path fill="#f8f9fb" d="M0,50 L0,15 Q720,50 1440,10 L1440,50 Z" />
        </svg>
      </div>

      <div className="bg-[#f8f9fb] py-10 sm:py-14 px-4">
        <div className="max-w-2xl mx-auto">

          {submitted ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center" data-testid="incident-success">
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={32} className="text-emerald-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Declaration envoyée</h2>
              <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
                Votre déclaration a été transmise au bureau de la SFOALC. Merci pour votre contribution à la sécurité des patients.
              </p>
              <div className="flex gap-3 justify-center">
                <button onClick={() => navigate('/')} className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all" data-testid="btn-home">
                  Retour à l'accueil
                </button>
                <button onClick={() => { setSubmitted(false); setForm({ ...form, category: '', severity: '', description: '', consequences: '', actions_taken: '' }); }} className="px-5 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-sm font-semibold hover:bg-[#162d4a] transition-all" data-testid="btn-new-incident">
                  Nouvelle déclaration
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="incident-form">

              {/* Info banner */}
              <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4 border border-blue-100">
                <Info size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 leading-relaxed">
                  Cette déclaration est confidentielle. Les informations sont utilisées à des fins de pharmacovigilance et d'amélioration des pratiques.
                </p>
              </div>

              {/* Category */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">
                  Type d'incident *
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {categories.map(cat => (
                    <button
                      key={cat} type="button"
                      onClick={() => updateForm('category', cat)}
                      className={`px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all ${
                        form.category === cat
                          ? 'border-[#1e3a5f] bg-blue-50 text-[#1e3a5f]'
                          : 'border-gray-100 text-gray-500 hover:border-gray-300'
                      }`}
                      data-testid={`cat-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                    >{cat}</button>
                  ))}
                </div>
              </div>

              {/* Severity */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">
                  Gravite *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {severities.map(s => (
                    <button
                      key={s.value} type="button"
                      onClick={() => updateForm('severity', s.value)}
                      className={`p-3 rounded-xl border-2 text-sm font-bold transition-all ${
                        form.severity === s.value ? s.color + ' border-current' : 'border-gray-100 text-gray-400 hover:border-gray-300'
                      }`}
                      data-testid={`severity-${s.value}`}
                    >{s.label}</button>
                  ))}
                </div>
              </div>

              {/* Patient + Date */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5">Initiales patient</label>
                    <input type="text" value={form.patient_initials} onChange={e => updateForm('patient_initials', e.target.value)}
                      placeholder="Ex: J.D." maxLength={10}
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] focus:ring-1 focus:ring-[#35AFE4]/20 outline-none" data-testid="input-initials" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5">Date de l'incident</label>
                    <input type="date" value={form.date_incident} onChange={e => updateForm('date_incident', e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] focus:ring-1 focus:ring-[#35AFE4]/20 outline-none" data-testid="input-date" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5">Type de lentille</label>
                    <input type="text" value={form.lens_type} onChange={e => updateForm('lens_type', e.target.value)}
                      placeholder="Souple, rigide, sclerale..."
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] focus:ring-1 focus:ring-[#35AFE4]/20 outline-none" data-testid="input-lens-type" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5">Marque / Modele</label>
                    <input type="text" value={form.lens_brand} onChange={e => updateForm('lens_brand', e.target.value)}
                      placeholder="Ex: Air Optix Night & Day"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] focus:ring-1 focus:ring-[#35AFE4]/20 outline-none" data-testid="input-lens-brand" />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5">Description de l'incident *</label>
                  <textarea value={form.description} onChange={e => updateForm('description', e.target.value)}
                    rows={4} placeholder="Décrivez l'incident en detail..."
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] focus:ring-1 focus:ring-[#35AFE4]/20 outline-none resize-none" data-testid="input-description" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5">Consequences</label>
                  <textarea value={form.consequences} onChange={e => updateForm('consequences', e.target.value)}
                    rows={2} placeholder="Consequences pour le patient..."
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] focus:ring-1 focus:ring-[#35AFE4]/20 outline-none resize-none" data-testid="input-consequences" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5">Actions entreprises</label>
                  <textarea value={form.actions_taken} onChange={e => updateForm('actions_taken', e.target.value)}
                    rows={2} placeholder="Mesures prises suite à l'incident..."
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] focus:ring-1 focus:ring-[#35AFE4]/20 outline-none resize-none" data-testid="input-actions" />
                </div>
              </div>

              {/* Reporter info */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-900 uppercase tracking-wider">Declarant</label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.anonymous} onChange={e => updateForm('anonymous', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-[#35AFE4] focus:ring-[#35AFE4]" data-testid="input-anonymous" />
                    <span className="text-xs text-gray-500 font-medium">Declaration anonyme</span>
                  </label>
                </div>
                {!form.anonymous && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Nom</label>
                      <input type="text" value={form.reporter_name} onChange={e => updateForm('reporter_name', e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] focus:ring-1 focus:ring-[#35AFE4]/20 outline-none" data-testid="input-reporter-name" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Email</label>
                      <input type="email" value={form.reporter_email} onChange={e => updateForm('reporter_email', e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] focus:ring-1 focus:ring-[#35AFE4]/20 outline-none" data-testid="input-reporter-email" />
                    </div>
                  </div>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-[#1e3a5f] hover:bg-[#162d4a] text-white font-bold text-sm transition-all hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="btn-submit-incident"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                {loading ? 'Envoi en cours...' : 'Envoyer la déclaration'}
              </button>
            </form>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default IncidentDeclarationPage;
